/**
 * Модель экрана агроциклов — чистые функции, без React и без запросов.
 *
 * Клиент НЕ выводит состояние работы: сервер отдаёт готовое `state`, потому что
 * правило «заблокирована → доступна → требуется → просрочена, а выполнено
 * возможно в любой момент» обязано быть одно на весь продукт. Здесь только
 * перевод кода в слова — и ни одного места, где код превращался бы в другой код.
 *
 * Общее правило файла: НЕИЗВЕСТНЫЙ код остаётся неизвестным. Ни одна функция не
 * подставляет «наверное, заблокирована» вместо кода, которого не знает: молча
 * названное чужим именем состояние хуже, чем честное «неизвестно».
 */

import type {
  CandidateRow,
  CatalogWorkOut,
  SlotOut,
  TemplateRow,
} from "../../api/client";
import { defineMessages, type PlainMessage } from "../../i18n/define";
import { tr } from "../../i18n/runtime";

const m = defineMessages({
  sBlocked: {
    id: "canonAgrocycle.model.state.blocked",
    defaultMessage: "заблокирована",
    description: "Состояние работы плана агроцикла (бейдж): работу ещё не открыли; строчными, коротко",
  },
  sAvailable: {
    id: "canonAgrocycle.model.state.available",
    defaultMessage: "доступна",
    description: "Состояние работы плана агроцикла (бейдж): открыта, срок ещё не наступил; строчными, коротко",
  },
  sRequired: {
    id: "canonAgrocycle.model.state.required",
    defaultMessage: "требуется",
    description: "Состояние работы плана агроцикла (бейдж): открыта и срок идёт; строчными, коротко",
  },
  sOverdue: {
    id: "canonAgrocycle.model.state.overdue",
    defaultMessage: "просрочена",
    description: "Состояние работы плана агроцикла (бейдж): срок прошёл, отчёта нет; строчными, коротко",
  },
  sDone: {
    id: "canonAgrocycle.model.state.done",
    defaultMessage: "выполнена",
    description: "Состояние работы плана агроцикла (бейдж): есть отчёт о выполнении; строчными, коротко",
  },
  sCancelled: {
    id: "canonAgrocycle.model.state.cancelled",
    defaultMessage: "отменена",
    description: "Состояние работы плана агроцикла (бейдж): отменена решением; строчными, коротко",
  },
  hBlocked: {
    id: "canonAgrocycle.model.state.blocked.hint",
    defaultMessage: "работу ещё не открыли — хозяйство её не видит",
    description: "Подсказка у состояния «заблокирована»",
  },
  hAvailable: {
    id: "canonAgrocycle.model.state.available.hint",
    defaultMessage: "открыта, но срок ещё не требует её выполнения",
    description: "Подсказка у состояния «доступна»",
  },
  hRequired: {
    id: "canonAgrocycle.model.state.required.hint",
    defaultMessage: "открыта, срок идёт — работу ждут",
    description: "Подсказка у состояния «требуется»",
  },
  hOverdue: {
    id: "canonAgrocycle.model.state.overdue.hint",
    defaultMessage: "срок прошёл, отчёта о выполнении нет",
    description: "Подсказка у состояния «просрочена»",
  },
  hDone: {
    id: "canonAgrocycle.model.state.done.hint",
    defaultMessage: "есть отчёт о выполнении",
    description: "Подсказка у состояния «выполнена»",
  },
  hCancelled: {
    id: "canonAgrocycle.model.state.cancelled.hint",
    defaultMessage: "отменена в этом агроцикле решением с причиной",
    description: "Подсказка у состояния «отменена»",
  },
  noData: {
    id: "canonAgrocycle.model.noData",
    defaultMessage: "нет данных",
    description: "Подпись, когда данных нет (это не ноль); строчными, коротко",
  },
  lDraft: {
    id: "canonAgrocycle.model.template.draft",
    defaultMessage: "черновик",
    description: "Статус шаблона агроцикла (бейдж): правится свободно; строчными, коротко",
  },
  lPublished: {
    id: "canonAgrocycle.model.template.published",
    defaultMessage: "действует",
    description: "Статус шаблона агроцикла (бейдж): по нему можно начинать агроциклы; строчными, коротко",
  },
  lArchived: {
    id: "canonAgrocycle.model.template.archived",
    defaultMessage: "в архиве",
    description: "Статус шаблона агроцикла (бейдж): по нему больше не начинают; строчными, коротко",
  },
  lDraftHint: {
    id: "canonAgrocycle.model.template.draftHint",
    defaultMessage: "правится свободно; начинать по нему агроциклы нельзя",
    description: "Подсказка у статуса «черновик»",
  },
  lPublishedHint: {
    id: "canonAgrocycle.model.template.publishedHint",
    defaultMessage: "не меняется: по нему уже могут идти начатые агроциклы",
    description: "Подсказка у статуса «действует»",
  },
  lArchivedHint: {
    id: "canonAgrocycle.model.template.archivedHint",
    defaultMessage: "по нему больше не начинают; прошлые агроциклы на него ссылаются",
    description: "Подсказка у статуса «в архиве»",
  },
  wNone: {
    id: "canonAgrocycle.model.window.none",
    defaultMessage: "без срока",
    description: "Срок работы плана: срока нет (строчными, коротко)",
  },
  wNoneHint: {
    id: "canonAgrocycle.model.window.noneHint",
    defaultMessage: "срока нет — просрочки по этой работе не бывает",
    description: "Подсказка к «без срока»",
  },
  wUnresolved: {
    id: "canonAgrocycle.model.window.unresolved",
    defaultMessage: "срок не посчитан",
    description: "Срок работы плана: срок зависит от события, которого ещё нет (строчными, коротко)",
  },
  wUnresolvedHint: {
    id: "canonAgrocycle.model.window.unresolvedHint",
    defaultMessage: "форма срока считается от внешнего события, которого ещё нет",
    description: "Подсказка к «срок не посчитан»",
  },
  wRange: {
    id: "canonAgrocycle.model.window.range",
    defaultMessage: "{from} — {to}",
    description: "Срок работы плана; {from}, {to} — даты или «…», если граница не задана",
  },
  wShifted: {
    id: "canonAgrocycle.model.window.shifted",
    defaultMessage: "срок сдвинут отклонением этого агроцикла",
    description: "Подсказка к сроку: он сдвинут решением по этому агроциклу",
  },
  aBlock: {
    id: "canonAgrocycle.model.action.block",
    defaultMessage: "Заблокировать",
    description: "Кнопка: закрыть работу плана для хозяйства (менеджер хокимията); коротко",
  },
  aOpen: {
    id: "canonAgrocycle.model.action.open",
    defaultMessage: "Открыть",
    description: "Кнопка: открыть работу плана для хозяйства (менеджер хокимията); коротко",
  },
  startReady: {
    id: "canonAgrocycle.model.start.ready",
    defaultMessage: "можно начинать",
    description: "Готовность: агроцикл на этом контуре можно начать (бейдж; строчными, коротко)",
  },
  startWindow: {
    id: "canonAgrocycle.model.start.window",
    defaultMessage: "окно начала: {from} — {to}",
    description: "Подсказка к готовности; {from}, {to} — даты, между которыми агроцикл можно начать",
  },
  startBlocked: {
    id: "canonAgrocycle.model.start.blocked",
    defaultMessage: "начать нельзя",
    description: "Готовность: агроцикл пока начать нельзя (бейдж; строчными, коротко)",
  },
  frozen: {
    id: "canonAgrocycle.model.template.frozen",
    defaultMessage: "{count, plural, one {По этому шаблону идёт # агроцикл} few {По этому шаблону идут # агроцикла} many {По этому шаблону идут # агроциклов} other {По этому шаблону идут # агроцикла}}",
    description: "Бейдж у шаблона: сколько агроциклов уже идёт по нему (поэтому его нельзя менять)",
  },
});

export const SLOT_STATE_LABELS: Record<string, PlainMessage> = {
  blocked: m.sBlocked,
  available: m.sAvailable,
  required: m.sRequired,
  overdue: m.sOverdue,
  done: m.sDone,
  cancelled: m.sCancelled,
};

export const SLOT_STATE_HINTS: Record<string, PlainMessage> = {
  blocked: m.hBlocked,
  available: m.hAvailable,
  required: m.hRequired,
  overdue: m.hOverdue,
  done: m.hDone,
  cancelled: m.hCancelled,
};

/** Состояния, которые требуют движения от хозяйства прямо сейчас. */
const ATTENTION_STATES = new Set(["required", "overdue"]);

/** Порядок показа состояний — от «ещё не начали» к «уже закрыли». */
export const SLOT_STATE_ORDER = [
  "blocked",
  "available",
  "required",
  "overdue",
  "done",
  "cancelled",
] as const;

export interface StateView {
  label: string;
  hint: string | null;
  known: boolean;
  attention: boolean;
}

export function slotStateView(code: string | null | undefined): StateView {
  if (!code) {
    return { label: tr(m.noData), hint: null, known: false, attention: false };
  }
  const label = SLOT_STATE_LABELS[code];
  if (label === undefined) {
    return { label: code, hint: null, known: false, attention: false };
  }
  const hint = SLOT_STATE_HINTS[code];
  return {
    label: tr(label),
    hint: hint ? tr(hint) : null,
    known: true,
    attention: ATTENTION_STATES.has(code),
  };
}

export const LIFECYCLE_LABELS: Record<string, PlainMessage> = {
  draft: m.lDraft,
  published: m.lPublished,
  archived: m.lArchived,
};

export interface LifecycleView {
  label: string;
  known: boolean;
  /** Правится ли версия. Опубликованную держит база, а не аккуратность. */
  editable: boolean;
  hint: string;
}

export function lifecycleView(code: string | null | undefined): LifecycleView {
  if (code === "draft") {
    return {
      label: tr(m.lDraft),
      known: true,
      editable: true,
      hint: tr(m.lDraftHint),
    };
  }
  if (code === "published") {
    return {
      label: tr(m.lPublished),
      known: true,
      editable: false,
      hint: tr(m.lPublishedHint),
    };
  }
  if (code === "archived") {
    return {
      label: tr(m.lArchived),
      known: true,
      editable: false,
      hint: tr(m.lArchivedHint),
    };
  }
  return {
    label: code ?? tr(m.noData),
    known: false,
    editable: false,
    hint: "",
  };
}

export interface WindowView {
  label: string;
  hint: string | null;
  /** Известен ли срок вообще. `false` — показывать как «нет данных». */
  known: boolean;
}

/**
 * Срок работы словами. Три «пустых» случая различаются, и это главное здесь.
 *
 * «Срока нет» означает, что просрочки не будет никогда. «Срок не посчитан»
 * означает, что мы её просто не видим — форма срока требует внешнего якоря,
 * которого пока нет. Слить их в одно «—» значило бы спрятать вторую половину
 * работ от того, кто отвечает за сроки.
 */
export function windowView(slot: {
  window_from: string | null;
  window_to: string | null;
  window_basis: string;
}): WindowView {
  if (slot.window_basis === "none") {
    return {
      label: tr(m.wNone),
      hint: tr(m.wNoneHint),
      known: true,
    };
  }
  if (slot.window_basis === "unresolved") {
    return {
      label: tr(m.wUnresolved),
      hint: tr(m.wUnresolvedHint),
      known: false,
    };
  }
  if (slot.window_from === null && slot.window_to === null) {
    return { label: tr(m.noData), hint: null, known: false };
  }
  const shifted = slot.window_basis === "shifted";
  return {
    label: tr(m.wRange, { from: slot.window_from ?? "…", to: slot.window_to ?? "…" }),
    hint: shifted ? tr(m.wShifted) : null,
    known: true,
  };
}

/**
 * Какое решение доступно по слоту: открыть закрытое или закрыть открытое.
 *
 * Повтор того же решения сервер отклоняет, поэтому кнопка всегда ровно одна —
 * иначе человек нажимал бы «открыть» на открытом и получал отказ вместо
 * действия. Одно движение вместо двух, и это ровно тот счёт, по которому в
 * системе учёта меряется удобство.
 */
export function availabilityAction(slot: SlotOut): {
  action: "open" | "block";
  label: string;
  needsReason: boolean;
} | null {
  if (slot.work_def_id === null) return null;
  if (slot.availability === "open") {
    return { action: "block", label: tr(m.aBlock), needsReason: true };
  }
  return { action: "open", label: tr(m.aOpen), needsReason: false };
}

export interface StateBucket {
  code: string;
  label: string;
  count: number;
}

/**
 * Разбор плана по состояниям в фиксированном порядке.
 *
 * Ноль остаётся в списке намеренно: «просроченных нет» и «про просроченные не
 * сказано» читаются по-разному, а второе прячет ось целиком. Неизвестные коды
 * добавляются в хвост, а не отбрасываются — иначе сумма столбика однажды
 * перестала бы сходиться с числом работ, и никто бы не заметил.
 */
export function stateBuckets(byState: Record<string, number>): StateBucket[] {
  const known: StateBucket[] = SLOT_STATE_ORDER.map((code) => ({
    code,
    label: tr(SLOT_STATE_LABELS[code] as PlainMessage),
    count: byState[code] ?? 0,
  }));
  const extra = Object.keys(byState)
    .filter((code) => !SLOT_STATE_ORDER.includes(code as never))
    .sort()
    .map((code) => ({ code, label: code, count: byState[code] as number }));
  return [...known, ...extra];
}

/** Сколько работ в плане требуют движения прямо сейчас. */
export function demanding(byState: Record<string, number>): number {
  return (byState.required ?? 0) + (byState.overdue ?? 0);
}

export interface StartHint {
  /** Можно ли запускать на выбранную дату. */
  ready: boolean;
  label: string;
  hint: string;
}

/**
 * Подсказка о запуске — ДО клика, а не вместо отказа.
 *
 * Экран, который заранее говорит «запуск возможен с 1 марта по 2 мая»,
 * экономит человеку попытку, отказ и чтение отказа. Причина отказа приходит с
 * сервера готовой: придумывать её здесь значило бы завести второе правило
 * сезона рядом с тем, которое действительно решает.
 */
export function startHint(row: CandidateRow): StartHint {
  if (row.may_start) {
    return {
      ready: true,
      label: tr(m.startReady),
      hint: row.guard
        ? tr(m.startWindow, { from: row.guard.start_from, to: row.guard.start_to })
        : "",
    };
  }
  return {
    ready: false,
    label: tr(m.startBlocked),
    hint: row.blocked_reason || row.guard_note || "",
  };
}

/**
 * Строка о том, сколько циклов заморозило версию шаблона.
 *
 * Это не украшение: ненулевое число — прямая причина, по которой версию нельзя
 * править. Человек должен прочитать её раньше, чем начнёт искать кнопку
 * «изменить».
 */
export function frozenNote(row: TemplateRow): string {
  if (row.instances === 0) return "";
  return tr(m.frozen, { count: row.instances });
}

/** Каталог по категориям: работа по сути живёт вне культуры. */
export function catalogByCategory(
  items: CatalogWorkOut[],
): { category: string; items: CatalogWorkOut[] }[] {
  const groups = new Map<string, CatalogWorkOut[]>();
  for (const item of items) {
    const bucket = groups.get(item.category);
    if (bucket) bucket.push(item);
    else groups.set(item.category, [item]);
  }
  return [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([category, group]) => ({ category, items: group }));
}

/**
 * Нормы на гектар — или честное «не заданы».
 *
 * Владелец ещё не зафиксировал техкарты, и подставить сюда числа из черновика
 * значило бы выдать гипотезу за норматив. Пустые нормы показываются словами, а
 * не нулём.
 */
export function normsView(norms: Record<string, unknown>): {
  empty: boolean;
  entries: { key: string; value: string }[];
} {
  const entries = Object.entries(norms).map(([key, value]) => ({
    key,
    value: String(value),
  }));
  return { empty: entries.length === 0, entries };
}
